/**
 * Clerk session claims — single source of truth para los valores del JWT template.
 *
 * El template de Clerk publica claims desde `user.public_metadata.*` (más email
 * built-in). Esto permite leerlos del token (rápido, sin fetch a Clerk API) en
 * vez de tener que ir contra `useUser().user.publicMetadata`.
 *
 * Producers (escriben publicMetadata):
 *   - api/lib/userMemberships.ts:syncMembershipToClerk → membership_level,
 *     devil_dark_ui, approved, telegram_invite_url, membership_updated_at
 *   - api/admin/notify-pending-subscriber.ts → banned, banned_at, ban_reason
 *   - role / super_admin / is_super_admin → Clerk Dashboard manual (no writer
 *     en código todavía).
 *
 * JWT Template (Clerk) — recommended claims (Dashboard → Sessions → Customize):
 *   {
 *     "role": "{{user.public_metadata.role}}",
 *     "status": "{{user.public_metadata.status}}",
 *     "approved": "{{user.public_metadata.approved}}",
 *     "membership_level": "{{user.public_metadata.membership_level}}",
 *     "membership_status": "{{user.public_metadata.membership_status}}"
 *   }
 * Common users (post-pay, waiting admin): role="user", status="pending",
 * approved=false, membership_status="pending_approval". Never leave public_metadata `{}`.
 * Other fields fall back to full publicMetadata via useUser().
 *
 * Consumers principales:
 *   - useClerkSessionClaims() (src/lib/auth.ts) — hook canónico
 *   - BannedGate, MiAcceso, PendingApproval, MembresiaPremiumVip
 */

export type MembershipTierCode = string;

export type ClerkUserRole = "admin" | "super_admin" | "user";

export interface ClerkSessionClaims {
  /** Tier de membresía: vip | platinum | devil | public | ... — fuente: membership_level (con alias `lvl`). */
  membershipLevel?: MembershipTierCode;
  /** Role canónico. Si `super_admin`/`is_super_admin` es true se eleva a "super_admin". */
  role?: ClerkUserRole;
  /** True cuando un admin (o el flujo anti-abuso) banea al usuario. Gate global lo enforza. */
  banned?: boolean;
  /** Admin aprobó la membresía y ya puede ver `/portal/mi-acceso`. */
  approved?: boolean;
  /** Cualquiera de `super_admin` o `is_super_admin` === true. */
  superAdmin?: boolean;
  /** Activa el theme oscuro reforzado para tier "devil". */
  devilDarkUi?: boolean;
  /** Link privado del grupo Telegram, asignado al aprobar. */
  telegramInviteUrl?: string;
  /** Email primario del usuario (Clerk built-in, no metadata). */
  email?: string;
  /** General status from public_metadata.status (e.g. "active", "pending", "rejected").
   *  Exposed via JWT template: `"status": "{{user.public_metadata.status}}"`. */
  status?: string;
  /** DB-aligned status: pending_approval | active | rejected | … */
  membershipStatus?: string;
}

/** Detecta strings que vienen del template sin resolver (`{{user.public_metadata.foo}}`). */
function isUnresolvedTemplate(value: string): boolean {
  return value.startsWith("{{") && value.endsWith("}}");
}

/** Clerk JWT templates a veces stringifican booleans (`"true"`, `"false"`, `""`). */
function coerceBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (isUnresolvedTemplate(value)) return undefined;
    const v = value.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
    if (v === "") return undefined;
  }
  return undefined;
}

function coerceString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const v = value.trim();
  if (!v || isUnresolvedTemplate(v)) return undefined;
  return v;
}

function coerceRole(value: unknown): ClerkUserRole | undefined {
  const s = coerceString(value);
  if (!s) return undefined;
  const v = s.toLowerCase();
  if (v === "admin" || v === "super_admin" || v === "user") return v;
  return undefined;
}

/** Check if any of the common Clerk role indicators in metadata/claims mark a super admin. */
function extractSuperAdminFlag(raw: Record<string, unknown>): boolean {
  const direct =
    coerceBoolean(raw.super_admin) === true ||
    coerceBoolean(raw.is_super_admin) === true ||
    coerceBoolean(raw.superAdmin) === true ||
    coerceBoolean(raw.isSuperAdmin) === true;

  // Support roles as array: ["super_admin", "admin"] or roles.super_admin etc.
  const roles = raw.roles;
  if (Array.isArray(roles)) {
    for (const r of roles) {
      if (coerceString(r)?.toLowerCase() === "super_admin") return true;
    }
  } else if (roles && typeof roles === "object") {
    if (coerceBoolean((roles as any).super_admin) === true) return true;
  }

  // Also check if role itself is super_admin (some people put it only in role)
  const role = coerceString(raw.role)?.toLowerCase();
  if (role === "super_admin") return true;

  return direct;
}

/**
 * Normaliza un objeto crudo (sessionClaims del JWT o publicMetadata) a la shape
 * tipada de `ClerkSessionClaims`. Soporta tanto las keys del template
 * (`lvl`, `membership_level`, `super_admin`, `is_super_admin`, `devil_dark_ui`,
 * `telegram_invite_url`, `email`) como las que pueda emitir Clerk built-in.
 */
export function parseClerkSessionClaims(
  raw: Record<string, unknown> | null | undefined,
): ClerkSessionClaims {
  if (!raw || typeof raw !== "object") return {};

  const membershipLevel =
    coerceString(raw.membership_level) ??
    coerceString(raw.membershipLevel) ??
    coerceString(raw.lvl);

  const explicitRole = coerceRole(raw.role);
  const superAdmin = extractSuperAdminFlag(raw);

  // Detect plain admin flag as well (for regular admin role)
  const adminFlag =
    coerceBoolean(raw.admin) === true ||
    coerceBoolean(raw.is_admin) === true;

  // Si el flag de super_admin es true elevamos el rol — equivale al check
  // que hace api/lib/clerkAuth.ts (role admin + super_admin flag === super_admin).
  let role: ClerkUserRole | undefined = superAdmin
    ? "super_admin"
    : explicitRole;

  // If no explicit role but we have admin flag, treat as admin
  if (!role && adminFlag) {
    role = "admin";
  }

  // Default common users to "user" role (explicit in publicMetadata for JWT template)
  if (!role) {
    role = "user";
  }

  // Ensure symmetry: if role is super_admin, superAdmin flag should be true
  const finalSuperAdmin = superAdmin || role === "super_admin";

  const status = coerceString(raw.status) ?? "pending"; // common users start as pending (per system flow)
  const membershipStatus =
    coerceString(raw.membership_status) ??
    coerceString(raw.membershipStatus) ??
    coerceString(raw.pipeline_stage) ??
    (coerceBoolean(raw.approved) === true
      ? "active"
      : coerceBoolean(raw.rejected) === true
        ? "rejected"
        : "pending_approval");

  return {
    membershipLevel,
    role,
    banned: coerceBoolean(raw.banned),
    approved: coerceBoolean(raw.approved),
    superAdmin: finalSuperAdmin || undefined,
    devilDarkUi:
      coerceBoolean(raw.devil_dark_ui) ?? coerceBoolean(raw.devilDarkUi),
    telegramInviteUrl:
      coerceString(raw.telegram_invite_url) ??
      coerceString(raw.telegramInviteUrl),
    email: coerceString(raw.email) ?? coerceString(raw.primary_email_address),
    status,
    membershipStatus,
  };
}

/** True si el tier representa una membresía activa (no `public`/vacío). */
export function isActiveMembershipLevel(
  level: MembershipTierCode | undefined | null,
): boolean {
  if (!level) return false;
  const v = String(level).trim().toLowerCase();
  if (!v) return false;
  return v !== "public" && v !== "none" && v !== "free";
}
